import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { LanguageProvider, useLanguage } from "../../contexts/LanguageContext";
import { Home, FileText, Wallet, LogOut, Plus, Share2, User, Wrench } from "lucide-react";
import NotificationBell from "../NotificationBell";

const TAB_DEFS = [
  { to: "/sc", icon: Home, key: "home", end: true },
  { to: "/sc/leads", icon: FileText, key: "leads" },
  { to: "/sc/service-requests", icon: Wrench, key: "service", testid: "service" },
  { to: "/sc/earnings", icon: Wallet, key: "payouts" },
  { to: "/sc/marketing", icon: Share2, key: "toolkit", testid: "toolkit" },
  { to: "/sc/profile", icon: User, key: "profile" },
];

function ScLayoutInner() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const nav = useNavigate();
  const location = useLocation();
  // The Service Requests page has its own full-width "Raise Service Request" CTA —
  // showing the global "+ New Lead" FAB there too overlaps ticket rows and is confusing.
  const hideFab = location.pathname.startsWith("/sc/service-requests");
  const TABS = TAB_DEFS.map((d) => ({ ...d, label: t(`nav.${d.key}`) }));
  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}
    >
      <div className="max-w-md mx-auto pb-24 min-h-screen relative bg-slate-50 shadow-xl">
        {/* Top bar — clean white glass header, brand always unmistakable */}
        <div className="sticky top-0 z-20 h-16 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-[0_2px_10px_rgba(15,23,42,0.05)]">
          <div className="px-4 h-full flex items-center justify-between">
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/purvodaya-logo.png" alt="Purvodaya Solar" className="w-9 h-9 object-contain flex-shrink-0" draggable={false} />
              <div className="min-w-0">
                <div className="leading-none whitespace-nowrap" data-testid="sc-brand-wordmark">
                  <span className="text-[#1B5E20] font-bold text-[15px] tracking-tight">Purvodaya </span>
                  <span className="text-[#D97706] font-extrabold text-[15px] tracking-tight">SOLAR</span>
                </div>
                <div className="text-[10px] font-semibold text-slate-500 mt-0.5">
                  SC Portal · {t("home.greeting")}, {user?.name?.split(" ")[0] || "there"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <NotificationBell buttonClassName="text-slate-500 hover:text-[#1B5E20]" />
              <button
                onClick={async () => { if (!window.confirm(t("profile.logoutConfirm"))) return; await logout(); nav("/sc/signup"); }}
                className="p-2 text-slate-500 hover:text-[#1B5E20]"
                data-testid="sc-logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 pt-4">
          <Outlet />
        </div>

        {/* Floating "New Lead" CTA */}
        {!hideFab && (
          <NavLink
            to="/sc/new-lead"
            data-testid="sc-fab-new-lead"
            className="fixed bottom-20 right-4 z-30 bg-[#1B5E20] text-white shadow-[0_4px_16px_rgba(27,94,32,0.4)] rounded-full p-4 hover:bg-[#15501A] hover:shadow-[0_6px_20px_rgba(27,94,32,0.5)] active:scale-90 transition-all"
            style={{ maxWidth: "calc(28rem - 1rem)" }}
          >
            <Plus className="w-6 h-6" strokeWidth={3} />
          </NavLink>
        )}

        {/* Bottom Tab Bar — white glass, executive green active state */}
        <div className="fixed bottom-0 left-0 right-0 z-20">
          <div className="max-w-md mx-auto bg-white/95 backdrop-blur-lg border-t border-slate-200/80 grid grid-cols-6 shadow-[0_-4px_16px_rgba(15,23,42,0.06)] px-1 py-1.5">
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                data-testid={`sc-tab-${tab.testid || tab.key}`}
                className={({ isActive }) =>
                  "flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all " +
                  (isActive
                    ? "bg-emerald-50 text-[#1B5E20]"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                <tab.icon className="w-4 h-4" strokeWidth={2.25} />
                {tab.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default function ScLayout() {
  return (
    <LanguageProvider persistEndpoint="/sc/profile">
      <ScLayoutInner />
    </LanguageProvider>
  );
}
